import { AxiosRequestConfig, AxiosResponse } from "axios";
// import { query } from "gql-query-builder";
import { query } from "gql-query-builder";
import AxiosActions from "../../AxiosActions";
import { getAxiosInstances } from "../../../../utils/AppUtils";
import { TListApiResponse } from "../commonTypes";
import { CampaignStatus, LabelTagOwnership, LabelTagType } from "../../../../utils/appEnums/AppEnums";
import { graphQlRequest } from "../graphql/graphql";
import store from "../../../../store";
import { toLabelTagType } from "../labelTags/labelTagsAPI";
import { uatSearchParamRegex } from "../../../../validators/validatorConstants";
// import { graphQlRequest } from "../graphql/graphql";

type TLabelTag = { id: number; name: string; type?: string };

type TAPILabelTag = {
    key: { root: "LabelTag" };
    attributes: {
        "ROOT.LabelTag.id": number;
        "ROOT.LabelTag.name": string;
        "ROOT.LabelTag.type"?: string | string[];
    };
    relationships: Record<string, unknown>;
    relationshipTotalCount: Record<string, unknown>;
};

// type TLabelTagGQLResponse = {
//     data: { LabelTag: { records: TLabelTag[] } };
// };

type TCreateCampainDto = {
    name: string;
    description: string;
    labelTagIds: number[]; // Label Tag instance id
    workflowTemplateGroupId: number; // Workflow Template Group instance id
    startDate: string; // LocalDateTime like TestProduct.activationDate
    allowMultiplePatientJourneys: boolean;
};

type TEditCampaignDto = {
    name: string;
    description: string;
    labelTags: {
        idsToAdd: number[]; // Label Tag instance ids
        idsToRemove: number[]; // Label Tag instance ids
    };
    workflowTemplateGroupId: number; // Workflow Template Group instance id
    startDate: string; // LocalDateTime like TestProduct.activationDate
    allowMultiplePatientJourneys: boolean;
};

type TCreateCampaignResponse = {
    id: number;
    name: string;
    message: string;
};

type TEditCampainResponse = {
    id: number;
    name: string;
    message: string;
};

type TCampaignStatistics = {
    failedRecords: number;
    processFinishedAt: string;
    processStartedAt: string;
    processedRecords: number;
    totalRecords: number;
};

export enum PartnerAccessLevels {
    READ = "Read",
    WRITE = "Write",
}

export type TCampaignInstance = {
    id: number;
    baseWorkflowTemplate: {
        id: number;
        name: string;
        designTimeWorkflowTemplateId: number;
        lastTimeStepConfigUpdated: string;
    } | null;
    name: string;
    description: string;
    startDate: Date;
    labelTags: TLabelTag[];
    allowMultiplePatientJourneys: boolean;
    status: CampaignStatus;
    statistics: TCampaignStatistics;
    cancellationReason: string;
    ownerOrganization: {
        id: number;
        name: string;
        tenantId: number;
    };
    sharedPartnerOrganizations:
        | {
              id: number;
              name: string;
              tenantId: number;
              shareAccess: PartnerAccessLevels;
              status: string;
          }[]
        | null;
    uiDetailsConfig: any; // NOTE: placeholder for a future business requirement; shape TBD
    workflowTemplateGroup: {
        id: number;
        name: string;
        created: Date;
        updated: Date;
        updatedBy: string;
        createdBy: {
            id: number;
            firstName: string;
            lastName: string;
        };
        published: {
            id: number;
            name: string;
            lastTimeStepConfigUpdated: string;
        };
    };
    inProgressProcess: {
        id: number;
        name: string;
        totalRecords: number;
        processedRecords: number;
        failedRecords: number;
    } | null;
    cohortDefinition: {
        id: number;
        name: string;
    } | null;
};

type TCampaignListInstance = {
    id: number;
    name: string;
    status: string;
    description: string;
    startDate: string;
    workflowTemplateGroupId: number;
    workflowTemplateGroupName: string;
    workflowCategory: string;
    labelTags: TLabelTag[];
    statistics?: TCampaignStatistics;
    processedRecords?: number | string;
    failedRecords?: number | string;
    updated: string;
    created: string;
    sharedPartnerOrganizations: TCampaignSharedPartnerOrganizations[];
    ownerOrganization: TCampaignOwnerOrganization;
};

interface IIdName {
    id: number;
    name: string;
}

interface IIdNameTenantId extends IIdName {
    tenantId: number;
}

interface IExternalSystem extends IIdName {
    externalSystemId: string;
}

type TJourneyListInstance = {
    id: number;
    orderId: number | null;
    isUat: boolean | null;
    comments: number;

    orderOrderingOrganizationId: number;
    orderOrderingOrganizationName: string;
    orderOrderingOrganizationTenantId: number;

    orderGtmOrganizationId: number | null;
    orderGtmOrganizationName: string | null;
    orderGtmOrganizationTenantId: string | null;
    orderGtmOrganizationLogoPublicUrl: string | null;

    orderPatientId: number | null;
    orderPatientSex: string | null;
    orderPatientRace: string | null;
    orderPatientEmail: string | null;
    orderPatientPhone: string | null;
    orderPatientLastName: string | null;
    orderPatientFirstName: string | null;
    orderPatientEthnicity: string | null;
    orderPatientDateOfBirth: string | null;

    memberId: number | null;
    memberSex: string | null;
    memberRace: string | null;
    memberEmail: string | null;
    memberPhone: string | null;
    memberLastName: string | null;
    memberFirstName: string | null;
    memberEthnicity: string | null;
    memberDateOfBirth: string | null;
    memberCounty: string | null;
    memberCountry: string | null;
    memberPostalCode: string | null;
    memberAddressCity: string | null;
    memberAddressStreet: string | null;

    templateOwnerOrganizationId: number | null;
    templateOwnerOrganizationName: string | null;
    templateOwnerOrganizationTenantId: number | null;
    templateOwnerOrganizationLogoPublicUrl: string | null;

    brandedTemplateOwnerOrganizationId: number | null;
    brandedTemplateOwnerOrganizationName: string | null;
    brandedTemplateOwnerOrganizationTenantId: string | null;
    brandedTemplateOwnerOrganizationLogoPublicUrl: string | null;

    type: string;
    status: string;

    tags: IIdName[];
    partnerTags: IIdName[];
    labOrganizations: IIdNameTenantId[];
    healthcareOrganizations: IIdNameTenantId[];
    goToMarketOrganizations: IIdNameTenantId[];
    supplierOrganizations: IIdNameTenantId[];
    externalSystems: IExternalSystem[];

    workflowCampaignId: number;
    workflowStepInProgressCanSubmit: boolean;
    workflowStepInProgressId: number;
    workflowStepInProgressName: string;
    workflowStepInProgressTime: number | null;
    workflowStepInProgressType: string;
    workflowCategory: string;

    nextWorkflowStepId: number | null;
    nextWorkflowStepName: string | null;
    nextWorkflowStepDueDate: string | null;

    updated: string;
    created: string;
    myOrganizationUserAssignments: { name: string; userId: number; personId: number }[];
    partnerOrganizationAssignments: { name: string; id: number; tenantId: number }[];
    memberInsuranceContractId: string | null;
    memberInsurancePolicyId: string | null;
    memberAddressState: string | null;
};

type TStatisticsConfig = {
    calendar: string;
    steps: {
        stepId: number;
        status: string;
    }[];
    journeyTags: { id: number }[];
};

type TStatisticsResponse = {
    tenantId: number;
    campaignId: number;
    calendar: string;
    steps: {
        stepId: number;
        stepName: string;
        statuses: {
            status: string;
            number: number;
            numberSinceLastPeriod: number;
        }[];
    }[];
    jouneyTags: {
        id: number;
        name: string;
        number: number;
        numberSinceLastPeriod: number;
    }[];
    total: { status: string; number: number }[];
};

export type TCampaignExecutionLog = {
    id: number;
    status: string;
    type: string;
    campaignId: number;
    // campaignName: string;
    executionBasedOnInstanceId: number;
    startedByPersonId: number;
    startedByPersonFirstName: string;
    startedByPersonLastName: string;
    startedByPersonEmail: string;
    startedByPersonPhone: string;
    totalRecords: number;
    processedRecords: number;
    successRecords: number;
    failedRecords: number;
    errorFileId: number | null;
    errorFileName: string | null;
    processStartedAt: string;
    processFinishedAt: string;
    updated: string;
    created: string;
};

export type TCampaignAuditLog = {
    id: number;
    name: string;
    type: string;
    content: string;
    ownerOrganizationId: number;
    ownerOrganizationName: string;
    ownerOrganizationTenantId: number;
    ownerPersonId: number;
    ownerPersonFirstName: string;
    ownerPersonLastName: string;
    workflowCampaignId: number;
    workflowCampaignName: string;
    workflowTemplateId: number;
    workflowTemplateName: string;
    workflowTemplateStepId: number;
    workflowTemplateStepName: string;
    updated: string;
    created: string;
};

type TCampaignOwnerOrganization = {
    id: number;
    name: string;
    tenantId: number;
};

type TCampaignSharedPartnerOrganizations = {
    id: number;
    tenantId: number;
    sharedPartnerOrganizationName: string;
    shareAccess: string;
};

export const getCampaignList = (dto, config: AxiosRequestConfig, spinner = false): Promise<AxiosResponse<TListApiResponse<TCampaignListInstance>>> => {
    return AxiosActions.post(getAxiosInstances().V3, "/health/grid/workflow-campaign", dto, config, spinner);
};

/**
 * @param config supports query param {{isUat}}: possible values: "Not UAT", "UAT", "All"
 * @returns if queryParam {{isUat}} is undefined - return Non UAT journeys
 */
export const getJourneyList = (dto, config: AxiosRequestConfig, spinner = false): Promise<AxiosResponse<TListApiResponse<TJourneyListInstance>>> => {
    if (!config?.params?.filter) {
        return AxiosActions.post(getAxiosInstances().V3, "/health/grid/journey", dto, config, spinner);
    }

    if (!config.params.filter.includes("isUat")) {
        return AxiosActions.post(getAxiosInstances().V3, "/health/grid/journey", dto, config, spinner);
    }

    const shallowConfig = { ...config };

    try {
        const isUatSearch = shallowConfig.params.filter.match(uatSearchParamRegex).groups.searchValue;
        const isSecondUatSearch = shallowConfig.params.filter.match(uatSearchParamRegex).groups.secondValue;
        shallowConfig.params.filter = shallowConfig.params.filter.replace(uatSearchParamRegex, "");
        shallowConfig.params.isUat = "All";

        if (isUatSearch === "true") {
            shallowConfig.params.isUat = "UAT";
        }

        if (isUatSearch === "false") {
            shallowConfig.params.isUat = "NOT Uat";
        }

        if (isSecondUatSearch) {
            shallowConfig.params.isUat = "All";
        }
    } catch {
        return AxiosActions.post(getAxiosInstances().V3, "/health/grid/journey", dto, shallowConfig, spinner);
    }
    return AxiosActions.post(getAxiosInstances().V3, "/health/grid/journey", dto, config, spinner);
};

export const createCampaign = (data: TCreateCampainDto, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse<TCreateCampaignResponse>> => {
    return AxiosActions.post(getAxiosInstances().V2, "/health/workflow-campaign", data, config, spinner);
};

export const getCampaignById = (campaignId: number, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse<TCampaignInstance>> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/workflow-campaign/${campaignId}`, config, spinner);
};

export const getCampaignStatistics = (
    campaignId: number,
    data: TStatisticsConfig,
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<AxiosResponse<TStatisticsResponse>> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/workflow-campaign/${campaignId}/dashboard`, data, config, spinner);
};

export const updateCampaignStatisticsConfig = (
    campaignId: number,
    data: { uiDetailsConfig: ReturnType<JSON["stringify"]> },
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<AxiosResponse> => {
    return AxiosActions.put(getAxiosInstances().V2, `/health/workflow-campaign/${campaignId}`, data, config, spinner);
};

export const updateCampaign = (
    campaignId: number,
    data: Partial<TEditCampaignDto>,
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<AxiosResponse<TEditCampainResponse>> => {
    return AxiosActions.put(getAxiosInstances().V2, `/health/workflow-campaign/${campaignId}`, data, config, spinner);
};

// const labelTagQuery = query({
//     operation: "LabelTag",
//     variables: {
//         sort: { type: "LabelTagSortInput", value: { fields: { attr: "name", order: "ASC" } } },
//         page: { type: "LabelTagPageInput", value: { limit: 200 } },
//     },
//     fields: [{ records: ["id", "name"] }],
// });

// export const getUniqueLabelTags = () => {
//     return graphQlRequest(labelTagQuery, false).then((res: AxiosResponse<TLabelTagGQLResponse>) => {
//         return res.data.data.LabelTag.records.map((e) => e.name).filter((e, i, ori) => i === ori.indexOf(e));
//     });
// };

export const getUniqueLabelTagsQuery = (
    labelTagType: LabelTagType | readonly LabelTagType[],
    labelTagOwnership: LabelTagOwnership,
): Promise<{ id: number; name: string; type?: string }[]> => {
    const tenantId = store.getState().userReducer.tenant.id;

    // Several types are OR-ed explicitly: /v2/query matches nothing for `type=in=(Import,Person)` on this value-list attribute.
    let filterCondition: string;
    if (Array.isArray(labelTagType)) {
        const typeConditions = labelTagType.map((type) => `type==${type}`).join(",");
        filterCondition = `(${typeConditions})`;
    } else {
        filterCondition = `type=in=${labelTagType}`;
    }
    // NOTE: if isActive status is needed in future add ';isActive==true' to the filterCondition
    if (labelTagOwnership === LabelTagOwnership.OWN) {
        filterCondition += `;createdByTenantId==${tenantId}`;
    } else if (labelTagOwnership === LabelTagOwnership.SHARED) {
        filterCondition += `;createdByTenantId!=${tenantId}`;
    }

    return AxiosActions.post(
        getAxiosInstances().V2,
        "/query",
        {
            key: "LabelTag",
            attributes: ["id", "name", "isActive", "type"],
            filter: filterCondition,
            limit: 1000,
        },
        {},
        false,
    ).then((res: AxiosResponse<TListApiResponse<TAPILabelTag>>) => {
        if (res.data?.data?.length) {
            const options = res.data.data.map((e) => ({
                name: e.attributes["ROOT.LabelTag.name"],
                id: e.attributes["ROOT.LabelTag.id"],
                type: toLabelTagType(e.attributes["ROOT.LabelTag.type"]),
            }));
            return options;
        }

        return [];
    });
};

export const getUniquePartnerLabelTags = (labelTagType: `${LabelTagType}`): Promise<string[]> => {
    return graphQlRequest(
        query({
            operation: "LabelTag",
            fields: [
                {
                    records: [
                        "id",
                        "name",
                        {
                            LabelTagIsOwnedByOrganization: [
                                {
                                    relations: [
                                        {
                                            record: ["id", "name"],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                "recordsCount",
            ],
            variables: {
                page: {
                    value: { limit: 10000, offset: 0 },
                    type: "LabelTagPageInput",
                },
                sort: {
                    value: { fields: { attr: "name", order: "ASC" } },
                    type: "LabelTagSortInput",
                },
                filter: {
                    value: { type: { equal: labelTagType }, createdByTenantId: { notEqual: store.getState().userReducer.tenant.id } },
                    type: "LabelTagFilterInput",
                },
            },
        }),
        false,
    ).then((res) => {
        // console.log(res);
        if (res.data) {
            return res.data.data.LabelTag.records;
            // .map((e) => `${e.name} - ${e.LabelTagIsOwnedByOrganization.relations[0].record.name}`)
            // .map((e) => e.name)
            // .filter((e, i, ori) => i === ori.indexOf(e))
        }

        return [];
    });
};

export const runCampaign = (id: number, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/workflow-campaign/${id}/run`, {}, config, spinner);
};

export const restartInterruptedCampaign = (id: number, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/workflow-campaign/${id}/restart`, {}, config, spinner);
};

export const getCampaignTaggedPatients = (id: number, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse<number>> => {
    return AxiosActions.get(getAxiosInstances().V2, `/health/workflow-campaign/${id}/tagged-instance-count`, config, spinner);
};

export const cancelCampaign = (
    campaignId: number,
    data: { cancellationReason: string },
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<AxiosResponse<TCreateCampaignResponse>> => {
    return AxiosActions.put(getAxiosInstances().V2, `/health/workflow-campaign/${campaignId}/cancel`, data, config, spinner);
};

export const completeCampaign = (campaignId: number, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse> => {
    return AxiosActions.put(getAxiosInstances().V2, `/health/workflow-campaign/${campaignId}/finish`, {}, config, spinner);
};

export const submitStepFromFile = (campaignId: number, stepId: number, data: any, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse> => {
    return AxiosActions.put(getAxiosInstances().V2, `/health/workflow-campaign/${campaignId}/step/${stepId}/batch/submit`, data, config, spinner);
};

export const getCampaignExecutionProcessLog = (
    dto,
    config: AxiosRequestConfig,
    spinner = false,
): Promise<AxiosResponse<TListApiResponse<TCampaignExecutionLog>>> => {
    return AxiosActions.post(getAxiosInstances().V3, "/health/grid/task-definition-record", dto, config, spinner);
};

export const getCampaignAuditProcessLog = (dto, config: AxiosRequestConfig, spinner = false): Promise<AxiosResponse<TListApiResponse<TCampaignAuditLog>>> => {
    return AxiosActions.post(getAxiosInstances().V3, "/health/grid/journey-log", dto, config, spinner);
};

export type TShareModifyAccessLevelCampaignDto = { targetAccessEntity: "Partner Organization"; targetEntityId: number; shareAccess: "Read" | "Write" }[];

export const shareModifyAccessLevelCampaigns = (
    campaignId: number,
    dto: TShareModifyAccessLevelCampaignDto,
    config: AxiosRequestConfig = {},
    spinner = true,
): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/workflow-campaign/${campaignId}/share`, dto, config, spinner);
};

export type TUnShareCampaignDto = { targetAccessEntity: "Partner Organization"; targetEntityId: number }[];

export const unShareCampaigns = (campaignId: number, dto: TUnShareCampaignDto, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse> => {
    return AxiosActions.post(getAxiosInstances().V2, `/health/workflow-campaign/${campaignId}/unshare`, dto, config, spinner);
};

export const updateCampaignWorkflowTemplate = (campaignId: number, config: AxiosRequestConfig = {}, spinner = true): Promise<AxiosResponse<"">> => {
    return AxiosActions.put(getAxiosInstances().V2, `/health/workflow-campaign/${campaignId}/use-latest-template`, {}, config, spinner);
};
